import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { navigationOwner } from '../../utils/navigation';

const BottomNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  // Keyboard detection for native platforms (iOS/Android)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let listeners: any[] = [];

    const setupListeners = async () => {
      // Use all keyboard events for maximum reliability
      listeners.push(
        await Keyboard.addListener('keyboardWillShow', () => setKeyboardOpen(true)),
        await Keyboard.addListener('keyboardDidShow', () => setKeyboardOpen(true)),
        await Keyboard.addListener('keyboardWillHide', () => setKeyboardOpen(false)),
        await Keyboard.addListener('keyboardDidHide', () => setKeyboardOpen(false))
      );
    };

    setupListeners();

    return () => {
      listeners.forEach(listener => listener?.remove());
    };
  }, []);

  // Hide while auth is still loading (prevents flash during loading screen)
  if (isLoading) return null;
  if (navigationOwner(location.pathname) === 'hidden') return null;

  // Don't show on auth, onboarding pages, or landing page (unauthenticated /)
  const hiddenPaths = ['/login', '/signup', '/verify', '/forgot-password', '/onboarding', '/birth-data', '/avatar-forge', '/go', '/questions', '/premium'];
  if (hiddenPaths.includes(location.pathname)) return null;
  // Hide on landing page when not authenticated
  if (location.pathname === '/' && !isAuthenticated) return null;
  // Also hide on public pages
  if (location.pathname.startsWith('/p/') || location.pathname.startsWith('/s/')) return null;
  // Hide when keyboard is open (iOS promo code input, etc.)
  if (keyboardOpen) return null;

  const tabs = [
    { path: '/dashboard', label: 'Today' },
    { path: '/profile', label: 'Soul Profile' },
    { path: '/circle', label: 'Circle' },
    { path: '/oracle', label: 'Oracle' },
    { path: '/journey', label: 'Journey' },
  ];

  const currentPath = location.pathname;

  return (
    <nav style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 100,
      display: 'flex',
      justifyContent: 'space-around',
      alignItems: 'center',
      paddingTop: '8px',
      paddingBottom: 'max(env(safe-area-inset-bottom, 8px), 8px)',
      background: 'rgba(10,10,15,0.95)',
      backdropFilter: 'blur(12px)',
      borderTop: '1px solid rgba(255,255,255,0.06)',
      transform: 'translateZ(0)',
      WebkitTransform: 'translateZ(0)',
    }}>
      {tabs.map(tab => {
        const isActive = navigationOwner(currentPath) === tab.path;
        
        return (
          <button
            key={tab.path}
            aria-current={isActive ? 'page' : undefined}
            onClick={() => navigate(tab.path)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '2px',
              padding: '8px 6px',
              minHeight: '44px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            <span style={{
              fontSize: '11px',
              fontWeight: isActive ? 600 : 400,
              color: isActive ? '#c9a84c' : 'rgba(255,255,255,0.3)',
              letterSpacing: '0.5px',
              transition: 'all 0.2s',
            }}>
              {tab.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
};

export default BottomNav;
